/**
 * Create Teabate (post-backed) — vertical composition.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "util/UserContext";
import { usePostFormLifecycle } from "hooks/usePostFormLifecycle";
import { useResolvedIssue, type IssueEntityInput } from "hooks/useResolvedIssue";
import { createTeabateForIssue } from "util/posts";
import { Impact } from "util/constants";
import InputTextArea from "ui/InputTextArea";
import TextEditor from "components/TextEditor";
import { Loader2 } from "lucide-react";
import { teabateCreateSchema } from "lib/validation/contentSchemas";
import PostFormShell from "components/Posts/PostFormShell";
import PrivacySection from "components/Posts/sections/PrivacySection";
import FormStatusMessage from "components/Posts/sections/FormStatusMessage";
import TeabateFields from "components/Posts/kindFields/TeabateFields";
import AiSuggestionPanel from "components/AI/AiSuggestionPanel";
import AiArgumentEvaluationPanel from "components/AI/AiArgumentEvaluationPanel";
import { PrivacyLevel } from "util/userUtils";

export interface TeabateFormHandle {
  submit: () => void;
}

interface Props {
  entity: IssueEntityInput;
  onSuccess: (impact: Impact) => void;
  onCancel: () => void;
  initialTitle?: string;
  hideActions?: boolean;
  onCanSubmitChange?: (canSubmit: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  navigateOnSuccess?: boolean;
  requireAlignmentDimension?: boolean;
  dimensionRequiredMessage?: string;
}

const TeabatePostForm = forwardRef<TeabateFormHandle, Props>(
  (
    {
      entity,
      onSuccess,
      onCancel,
      initialTitle = "",
      hideActions = true,
      onCanSubmitChange,
      onSubmittingChange,
      navigateOnSuccess = true,
      requireAlignmentDimension = false,
      dimensionRequiredMessage = "Please select a societal dimension.",
    },
    ref
  ) => {
    const navigate = useNavigate();
    const { currentUser, setShowAuthModal } = useUser();
    const { resolvedIssue: selectedIssue } = useResolvedIssue(entity);

    const [title, setTitle] = useState(initialTitle);
    const [argument, setArgument] = useState("");
    const [argumentType, setArgumentType] = useState<"agree" | "disagree">("agree");
    const [privacy, setPrivacy] = useState<PrivacyLevel>("public");
    const [selectedAlignment, setSelectedAlignment] = useState("");
    const [dimensionError, setDimensionError] = useState<string | null>(null);

    const lifecycle = usePostFormLifecycle({
      getValues: useCallback(
        () => ({
          title,
          hasIssue: Boolean(selectedIssue?.id),
        }),
        [title, selectedIssue]
      ),
      schema: teabateCreateSchema,
      isReady: Boolean(currentUser),
      onCanSubmitChange: undefined,
      onSubmittingChange,
    });

    useEffect(() => {
      const hasRequiredDimension = !requireAlignmentDimension || (selectedAlignment.length > 0 && selectedAlignment !== "none");
      onCanSubmitChange?.(lifecycle.canSubmit && hasRequiredDimension);
    }, [lifecycle.canSubmit, onCanSubmitChange, requireAlignmentDimension, selectedAlignment]);

    useEffect(() => {
      if (initialTitle) {
        setTitle(initialTitle);
      }
    }, [initialTitle]);

    const submit = useCallback(async () => {
      if (!currentUser) {
        setShowAuthModal(true);
        return;
      }
      const validationError = lifecycle.validateOrError();
      if (validationError) {
        lifecycle.setError(validationError);
        return;
      }
      if (requireAlignmentDimension && (!selectedAlignment || selectedAlignment === "none")) {
        const nextError = dimensionRequiredMessage;
        setDimensionError(nextError);
        lifecycle.setError(nextError);
        return;
      }

      lifecycle.beginSubmit();

      try {
        setDimensionError(null);
        const issueId = Number(selectedIssue?.id);
        const postData: {
          title: string;
          body: string;
          is_anonymous: boolean;
          rel_type: "impact_positive" | "impact_negative" | "impact_inactive";
          privacy: PrivacyLevel;
          properties?: { dimension: string };
          argument_type: "agree" | "disagree";
        } = {
          title: title.trim(),
          body: argument,
          is_anonymous: privacy === "anonymous",
          rel_type: "impact_inactive",
          privacy: privacy || "public",
          argument_type: argumentType,
        };
        if (selectedAlignment && selectedAlignment !== "none") {
          postData.properties = { dimension: selectedAlignment };
        }

        const newTeabate = await createTeabateForIssue(issueId, postData);

        onSuccess(newTeabate);

        if (navigateOnSuccess) {
          if (selectedAlignment && selectedAlignment !== "none") {
            navigate(`/teabate/${newTeabate.id}`, {
              state: { openAlignmentModal: true, argumentType },
            });
          } else {
            navigate(`/teabate/${newTeabate.id}`);
          }
        }
      } catch {
        lifecycle.setError("Failed to create teabate. Please try again.");
      } finally {
        lifecycle.endSubmit();
      }
    }, [
      argument,
      argumentType,
      currentUser,
      dimensionRequiredMessage,
      lifecycle,
      navigate,
      onSuccess,
      privacy,
      requireAlignmentDimension,
      selectedAlignment,
      selectedIssue,
      setShowAuthModal,
      title,
      navigateOnSuccess,
    ]);

    useImperativeHandle(ref, () => ({ submit }), [submit]);

    return (
      <PostFormShell intro="Start a debate around an issue. Provide a claim, your stance, and an argument backing it; optionally, allow users to add this claim to their perspective by setting a societal dimension.">
        {selectedIssue?.id && !title.trim() ? (
          <AiSuggestionPanel
            issueId={Number(selectedIssue.id)}
            onUseSuggestion={setTitle}
          />
        ) : null}

        <InputTextArea
          id="teabate-title"
          name="teabate-title"
          labelText="Your claim"
          fieldRequired
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={300}
        />

        <TeabateFields
          argumentType={argumentType}
          onArgumentTypeChange={setArgumentType}
          selectedAlignment={selectedAlignment}
          onAlignmentChange={(value) => {
            setSelectedAlignment(value);
            if (dimensionError && value && value !== "none") {
              setDimensionError(null);
            }
          }}
          requireAlignmentDimension={requireAlignmentDimension}
          dimensionError={dimensionError}
        />

        <div>
          <span className="block text-sm font-semibold mb-1">Argument</span>
          <TextEditor
            value={argument}
            onChange={setArgument}
            editorType="entity"
            placeholder="Reasoning for your stance"
          />
        </div>

        <AiArgumentEvaluationPanel
          claim={title}
          draftArgument={argument}
          issueId={selectedIssue?.id ? Number(selectedIssue.id) : undefined}
          onApplyRevision={setArgument}
          stance={argumentType}
        />

        <PrivacySection privacy={privacy} onPrivacyChange={setPrivacy} publicOnly miniIcon={false} />

        <FormStatusMessage error={lifecycle.error} />

        {!hideActions ? (
          <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
            <button type="button" onClick={onCancel} className="btn-secondary w-full sm:w-fit py-2 px-4">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!lifecycle.canSubmit || (requireAlignmentDimension && (!selectedAlignment || selectedAlignment === "none"))}
              className={`w-full sm:w-fit py-2 px-4 ${
                lifecycle.isSubmitting || (requireAlignmentDimension && (!selectedAlignment || selectedAlignment === "none"))
                  ? "btn-primary-disabled opacity-50 cursor-not-allowed"
                  : "btn-primary"
              }`}
            >
              {lifecycle.isSubmitting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Creating...
                </span>
              ) : (
                "Create Teabate"
              )}
            </button>
          </div>
        ) : null}
      </PostFormShell>
    );
  }
);

TeabatePostForm.displayName = "TeabatePostForm";

export default TeabatePostForm;
